import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ViewEncapsulation, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@app/services/auth.service';
import { EVENT_SCHEMA, EventTrackerInternalService } from '@app/services/event-tracker-2.service';
import { EventTrackerService, QiraEventHumanReadable } from '@app/services/event-tracker.service';
import { FeatureNoticeService } from '@app/services/feature-notice.service';
import { PrimeNgMessageService } from '@app/services/primeNgMessage.service';
import { TabService } from '@app/services/tab.service';
import { ChatTab, DisplayVehicle } from '@app/services/types';
import { CatalogSelectionComponent } from './components/catalog-selection/catalog-selection.component';
import { VinKbaSelectionComponent } from './components/vin-kba-selection/vin-kba-selection.component';
import { AnimationCompleteDirective } from './directives/animation-complete.directive';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { Carousel, CarouselModule } from 'primeng/carousel';

@Component({
  selector: 'app-vehicle-selection',
  standalone: true,
  imports: [CommonModule, TranslocoModule, CarouselModule, VinKbaSelectionComponent, CatalogSelectionComponent, AnimationCompleteDirective],
  template: `
    <div class="flex h-full w-full max-w-[calc(100dvw-1rem)] flex-col px-2 pb-4 pt-6 lg:w-[700px] lg:max-w-[calc(100dvw-2rem)] lg:px-6">
      <h2 class="text-2xl font-normal">{{ 'vehicleSelection.title' | transloco }}</h2>

      <!-- Navigation -->
      <div class="mt-4 flex items-center justify-between gap-16">
        <div class="flex space-x-2">
          <button
            (click)="goToSlide(0)"
            class="px-1 py-2 hover:bg-slate-50"
            [ngClass]="{
              'active border-b-2 border-blue-600 font-semibold text-blue-800': currentSlide() === 0,
              'text-gray-500 hover:bg-gray-100': currentSlide() !== 0,
            }"
            data-testid="vehicle-selection-tab-vinkba">
            {{ 'vehicleSelection.vin-kba' | transloco }}
          </button>
          <button
            (click)="goToSlide(1)"
            class="px-1 py-2 hover:bg-slate-50"
            [ngClass]="{
              'active border-b-2 border-blue-600 font-semibold text-blue-800': currentSlide() === 1,
              'text-gray-500 hover:bg-gray-100': currentSlide() !== 1,
            }"
            data-testid="vehicle-selection-tab-catalog">
            {{ 'vehicleSelection.catalog' | transloco }}
          </button>
        </div>
      </div>

      <!-- Carousel -->
      <p-carousel
        #carousel
        appAnimationComplete=".p-carousel-item-list"
        (animationComplete)="onCarouselAnimationComplete()"
        class="vehicle-selection-carousel mt-4 flex-1"
        [value]="slides()"
        [numVisible]="1"
        [numScroll]="1"
        [showNavigators]="false"
        [showIndicators]="false"
        [verticalViewPortHeight]="'100%'"
        (onPage)="onCarouselPageChange($event)">
        <ng-template let-element #item>
          <ng-container [ngSwitch]="element.type">
            <app-vin-kba-selection
              #vinKbaSelection
              *ngSwitchCase="'vin-kba'"
              (onSelect)="handleVehicleSelect($event)"
              class="block h-full px-[1px]"></app-vin-kba-selection>
            <app-catalog-selection
              #catalogSelection
              *ngSwitchCase="'catalog'"
              (onSelect)="handleVehicleSelect($event)"
              (onHintClick)="goToSlide(0)"
              class="block h-full px-[1px]"></app-catalog-selection>
          </ng-container>
        </ng-template>
      </p-carousel>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .vehicle-selection {
        & > div {
          @apply max-sm:h-full max-sm:w-full;
        }
      }
      .vehicle-selection-carousel {
        .p-carousel-content-container {
          height: 100%;
          .p-carousel-content {
            height: 100%;
            .p-carousel-item-list {
              height: 100%;
            }
          }
        }
        .p-inputtext:not(.p-invalid) {
          &:enabled:focus {
            @apply border-blue-600 ring-1 ring-blue-600;
          }
        }
        .p-inputtext.p-invalid {
          &:enabled:focus {
            @apply border-red-400 ring-0;
          }
        }
        /* Prevent tab navigation to inactive carousel items */
        .p-carousel-item:not(.p-carousel-item-active) {
          * {
            visibility: hidden !important;
          }
          button,
          input,
          select,
          textarea,
          a,
          [tabindex]:not([tabindex='-1']) {
            pointer-events: none !important;
            user-select: none !important;
          }
        }
      }
    `,
  ],
})
export class VehicleSelectionComponent {
  @Input() data: any;
  @Input() close: () => void = () => undefined;
  @ViewChild('carousel') carousel!: Carousel;
  @ViewChild('vinKbaSelection') vinKbaSelection!: VinKbaSelectionComponent;
  @ViewChild('catalogSelection') catalogSelection!: CatalogSelectionComponent;

  tabService = inject(TabService);
  router = inject(Router);
  messageService = inject(PrimeNgMessageService);
  featureNoticeService = inject(FeatureNoticeService);
  translocoService = inject(TranslocoService);
  eventTracker = inject(EventTrackerService);
  eventTrackerInternal = inject(EventTrackerInternalService);
  authService = inject(AuthService);

  slides = signal([{ type: 'vin-kba' }, { type: 'catalog' }]);
  currentSlide = signal(0);

  goToSlide(index: number): void {
    const previousSlide = this.currentSlide();
    this.currentSlide.set(index);

    if (this.carousel && previousSlide !== index) {
      const steps = index - previousSlide;
      if (steps > 0) {
        for (let i = 0; i < steps; i++) {
          this.carousel.navForward(new Event('click') as MouseEvent);
        }
      } else {
        for (let i = 0; i < Math.abs(steps); i++) {
          this.carousel.navBackward(new Event('click') as MouseEvent);
        }
      }
    }
  }

  onCarouselPageChange(event: any): void {
    // const currItem = this.slides[event.page];
    this.currentSlide.set(event.page);
    this.vinKbaSelection.hideDropdown();
    this.catalogSelection.hideDropdown();
  }

  onCarouselAnimationComplete(): void {
    this.vinKbaSelection.clear();
    this.catalogSelection.clear();
    const activeInput = document.querySelector('.p-carousel-item-active input') as HTMLElement;
    activeInput?.focus();
  }

  async handleVehicleSelect(vehicle: DisplayVehicle | undefined) {
    this.close();

    if (!vehicle) {
      console.error('[VehicleSelectionComponent] No vehicle selected, aborting tab creation.');
      Sentry.captureException(new Error('Missing vehicle'), {
        tags: {
          location: 'VehicleSelectionComponent',
          feature: 'QiraThreads',
          message: 'Creating new tab failed - no vehicle given.',
        },
      });
      return;
    }

    try {
      const newTab = await this.tabService.createTab(vehicle as DisplayVehicle);
      if (newTab) {
        this.router.navigate(['chat', newTab._id]);
        this._logEvent(QiraEventHumanReadable.VEHICLE_IDENTIFIED, newTab);
      }
    } catch (error) {
      this.authService.handleNotLoggedInError(error);

      await this.messageService.showError('vehicleSelection.error.message');

      console.error('[VehicleSelectionComponent] Creating new thread failed with', error);
      Sentry.captureException(error, {
        tags: {
          location: 'VehicleSelectionComponent',
          feature: 'QiraThreads',
          message: 'Creating new tab failed.',
        },
      });
    }
  }

  private _logEvent(event: QiraEventHumanReadable, tab: ChatTab): void {
    // deprecated event tracking
    this.eventTracker.logEvent(event, {
      thread_id: tab._id,
      vehicle_brand: tab.vehicle?.name,
      car_type_id: tab.vehicle?.id.carTypeId ?? tab.vehicle?.id.kTypeIdTecDoc ?? undefined,
      vin: tab.vehicleIdentification?.vin,
      kba: tab.vehicleIdentification?.kba,
      hp_make_id: tab.vehicleIdentification?.catalogue?.make.id,
      hp_model_id: tab.vehicleIdentification?.catalogue?.model.id,
    });

    this.eventTrackerInternal.trackEvent(EVENT_SCHEMA.CORE_PRODUCT.VEHICLE_ID.IDENTIFIED, {
      thread_id: tab._id,
      vehicle_brand: tab.vehicle?.name,
      car_id: tab.vehicle?.id.kTypeIdTecDoc,
      vin: tab.vehicleIdentification?.vin,
      kba: tab.vehicleIdentification?.kba,
      hp_make_id: tab.vehicleIdentification?.catalogue?.make.id,
      hp_model_id: tab.vehicleIdentification?.catalogue?.model.id,
    });
  }
}
